class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    /*
      Safari does not focus a link on mousedown — it blurs whatever had focus
      to <body>. A press on a line inside an open mega panel therefore reaches
      the inherited onFocusOut as focus leaving the menu, and the branch closed
      under the mouse button: the link was gone before mouseup, no click ever
      landed on it, and the menu read as if it swallowed every navigation
      (Charles, 2026-09-17). Chrome focuses the anchor instead, which is why a
      Chrome-only click harness saw all 58 links navigate.

      A press that starts inside this menu is not focus leaving it, so the
      close waits for the release instead of running under it. The release
      re-runs the inherited check, so a press that lands nowhere — the panel's
      own white space, a drag off the link — still closes the branch the way it
      always did. The mark lives on the element: three branches share this
      file, and a press inside one must not hold another one open.
    */
    this.pressed = false;
    this.addEventListener('pointerdown', () => {
      this.pressed = true;
    });

    /*
      The release has to be heard wherever the pointer comes back up, including
      outside this element, so it listens on `document` — and a document
      listener outlives the element unless it is removed. The header re-renders
      on every theme-editor edit, so the binding follows the element's life:
      bound in connectedCallback, removed in disconnectedCallback, under one
      reference held on the instance (removeEventListener only matches the
      identical function object).
    */
    this.release = () => {
      if (!this.pressed) return;
      this.pressed = false;
      super.onFocusOut();
    };

    /*
      Tab has to walk the panel, and the engine's own focus ring will not do it.
      Safari keeps <a> elements out of sequential focus navigation unless "Press
      Tab to highlight each item on a webpage" is turned on, so with a branch
      open Tab skipped all twelve links of the panel, landed on the NEXT branch
      summary — outside this element — and the inherited focusout closed the
      branch behind it (D-29 found the symptom, D-31 the cause: the same Tab
      ring skips a bare <a> on a page with no theme at all).

      So the walk is ours while one of our branches is open: forward through the
      panel's links in DOM order, backward with Shift, back to the summary off
      the first link, and past the last link the branch closes and focus carries
      on to whatever follows the header. Escape is left alone — `onKeyUpEscape`
      in global.js is bound to every `[id^="Details-"]` summary's parent, which
      is what a branch is, and it already closed the branch and handed focus
      back in both engines.

      Nothing here runs when the branch is closed, so the rest of the page keeps
      the ring the browser gives it. `DetailsDisclosure` is untouched: the share
      button, the footer disclosures and the localization selectors are not mega
      panels and have no links to walk.
    */
    this.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  connectedCallback() {
    document.addEventListener('pointerup', this.release);
    document.addEventListener('pointercancel', this.release);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerup', this.release);
    document.removeEventListener('pointercancel', this.release);
  }

  onFocusOut() {
    if (this.pressed) return;
    super.onFocusOut();
  }

  /* The panel's own links, in DOM order, skipping anything not rendered. */
  panelLinks() {
    if (!this.content) return [];
    return Array.from(this.content.querySelectorAll('a[href], button:not([disabled])')).filter(
      (el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length
    );
  }

  /* The first thing the browser would focus after this whole branch. */
  nextAfterTheBranch() {
    const candidates = Array.from(
      document.querySelectorAll(
        'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
    return candidates.find(
      (el) =>
        !this.contains(el) &&
        this.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING &&
        (el.offsetWidth || el.offsetHeight || el.getClientRects().length)
    );
  }

  onKeyDown(event) {
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
    if (!this.mainDetailsToggle.open) return;

    const summary = this.mainDetailsToggle.querySelector('summary');
    const links = this.panelLinks();
    if (!links.length) return;

    const active = document.activeElement;

    if (active === summary) {
      /* Backwards off the summary is leaving the menu: let it close normally. */
      if (event.shiftKey) return;
      event.preventDefault();
      links[0].focus();
      return;
    }

    const index = links.indexOf(active);
    if (index === -1) return;

    if (event.shiftKey) {
      event.preventDefault();
      (index === 0 ? summary : links[index - 1]).focus();
      return;
    }

    if (index < links.length - 1) {
      event.preventDefault();
      links[index + 1].focus();
      return;
    }

    /*
      Past the last link. The close has to happen before the move, or focus
      would sit in content the browser is about to hide and fall back to <body>
      — which would send the next Tab to the top of the document.
    */
    event.preventDefault();
    const next = this.nextAfterTheBranch();
    this.close();
    if (next) next.focus();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
